import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
} from '../styles';
import { Button, Container, Link, Svg } from './ui';
import logoIcon from '../images/logo-icon.inline.svg';
import twitterIcon from '../images/twitter-icon.inline.svg';
import linkedinIcon from '../images/linkedin-icon.inline.svg';
import facebookIcon from '../images/facebook-icon.inline.svg';
import instagramIcon from '../images/instagram-icon.inline.svg';
import isoIcon9001Icon from '../images/dizions-ISO-9001-2008-icon.png';
import cyberEssentialsIcon from '../images/cyberessentials-certification-mark-icon.svg';
import isoIcon27001Icon from '../images/dizions-ISO-27001-2013-icon.png';
import disabilityConfidentLogo from '../images/disability-confident-logo.svg';
import acevoLogo from '../images/acevo-premium-partner-logo.png';
import madeinukLogo from '../images/madeinuk-logo.svg';

const StyledFooter = styled.footer`
  color: ${standardColours.white};
  background-color: ${brandColours.quaternary};
`;

const StyledContainer = styled(Container)`
  ${maxBreakpointQuery.mlarge`
    padding-left: 0;
    padding-right: 0;
  `}
`;

const StyledInner = styled.div``;

const StyledTop = styled.div`
  padding-top: 60px;
  padding-bottom: 60px;
  text-align: center;

  ${maxBreakpointQuery.mlarge`
    padding-left: 30px;
    padding-right: 30px;
    border-bottom: 1px solid ${brandColours.senary};
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 80px;
    padding-bottom: 80px;
  `}

  ${minBreakpointQuery.mlarge`
    padding-top: 90px;
    padding-bottom: 100px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 110px;
  `}

  ${minBreakpointQuery.xlarge`
    padding-top: 130px;
  `}
`;

const StyledLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  width: 105px;
  height: 105px;
  background-color: ${standardColours.transparentBlack(0.15)};
  border-radius: 30px;
`;

const StyledLogo = styled(Svg)`
  height: 50px;
  width: 50px;
`;

const StyledCta = styled.div`
  margin-top: 48px;

  ${minBreakpointQuery.mlarge`
    max-width: 870px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 35px;
  `}
`;

const StyledCtaHeading = styled.h2`
  ${fluidFontSize(
    '35px',
    '70px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  font-weight: ${fontWeights.regular};

  strong {
    color: ${brandColours.senary};
    font-weight: ${fontWeights.regular};
  }
`;

const StyledCtaButton = styled(Button)`
  margin-top: 30px;

  ${minBreakpointQuery.mlarge`
    margin-top: 20px;
  `}
`;

const StyledMain = styled.div`
  padding-top: 45px;
  padding-bottom: 45px;
  display: grid;
  gap: 30px;

  ${maxBreakpointQuery.mlarge`
    padding-left: 30px;
    padding-right: 30px;
  `}

  ${minBreakpointQuery.small`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 55px;
    padding-bottom: 55px;
  `}

  ${minBreakpointQuery.mlarge`
    grid-template-columns: 200px 200px 1fr;
    padding-top: 0;
    padding-bottom: 80px;
  `}

  ${minBreakpointQuery.large`
    padding-bottom: 110px;
  `}

  ${minBreakpointQuery.xlarge`
    padding-bottom: 130px;
  `}
`;

const StyledGroup = styled.div`
  &:last-child {
    ${minBreakpointQuery.mlarge`
      grid-column: 4;
    `}
  }
`;

const StyledHeading = styled.h3`
  ${fontSize(16)}
`;

const StyledList = styled.ul`
  margin-top: 24px;
`;

const StyledListItem = styled.li`
  margin: 15px 0;
`;

const StyledListLink = styled(Link)``;

const StyledContact = styled.p`
  margin-top: 24px;
  white-space: break-spaces;
  line-height: 1.75;
`;

const StyledAccreditations = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 15px;

  ${maxBreakpointQuery.small`
    justify-content: space-around;
  `}

  ${minBreakpointQuery.small`
    gap: 20px;
  `}

  ${minBreakpointQuery.large`
    gap: 30px;
  `}

  &:nth-child(2), &:nth-child(3) {
    margin-top: 20px;

    ${minBreakpointQuery.small`
      margin-top: 25px;
    `}

    ${minBreakpointQuery.large`
      margin-top: 30px;
    `}
  }
`;

const StyledAccreditation = styled.img`
  max-height: 50px;

  ${minBreakpointQuery.small`
    max-height: 55px;
  `}

  ${minBreakpointQuery.medium`
    max-height: 60px;
  `}
  
  ${minBreakpointQuery.large`
    max-height: 65px;
  `}
`;

const StyledSocial = styled.div`
  margin-top: 40px;
  display: flex;
  flex-wrap: wrap;
  gap: 40px;

  ${maxBreakpointQuery.small`
    justify-content: space-around;
  `}
`;

const StyledSocialLink = styled.a``;

const StyledIcon = styled(Svg)`
  width: 24px;
  height: 24px;

  path {
    fill: ${brandColours.senary};
    opacity: 0.6;
  }
`;

const StyledBottom = styled.div`
  padding-bottom: 40px;

  ${maxBreakpointQuery.mlarge`
    padding-left: 30px;
    padding-right: 30px;
    text-align: center;
  `}

  ${minBreakpointQuery.mlarge`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `}
`;

const StyledCopyright = styled.p`
  color: ${brandColours.senary};
  ${fontSize(12)};
`;

const StyledSigniture = styled.p`
  color: ${brandColours.senary};
  ${fontSize(12)}

  ${maxBreakpointQuery.mlarge`
    margin-top: 25px;
  `}
`;

const Footer = () => {
  const {
    datoCmsSite: {
      globalSeo: { siteName },
    },
    datoCmsFooter: { ctaHeading, ctaLink, linkLists },
    datoCmsContactInformation: {
      address,
      phoneNumber,
      emailAddress,
      twitterProfile,
      linkedinProfile,
      facebookProfile,
      instagramProfile,
    },
  } = useStaticQuery(graphql`
    query FooterQuery {
      datoCmsSite {
        globalSeo {
          siteName
        }
      }
      datoCmsFooter {
        ctaHeading
        ctaLink {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        linkLists {
          heading
          links {
            text
            pageUrl {
              ...LinkFragment
            }
          }
        }
      }
      datoCmsContactInformation {
        address
        phoneNumber
        emailAddress
        twitterProfile
        linkedinProfile
        facebookProfile
        instagramProfile
      }
    }
  `);

  return (
    <StyledFooter>
      <StyledContainer>
        <StyledInner>
          <StyledTop>
            <StyledLogoWrapper>
              <StyledLogo image={logoIcon} />
            </StyledLogoWrapper>
            <StyledCta>
              <StyledCtaHeading
                dangerouslySetInnerHTML={{
                  __html: ctaHeading.replace(/(<p>|<\/p>)/g, ''),
                }}
              />
              <StyledCtaButton to={ctaLink.pageUrl}>
                {ctaLink.text}
              </StyledCtaButton>
            </StyledCta>
          </StyledTop>
          <StyledMain>
            {linkLists.map(({ heading, links }, i) => (
              <StyledGroup key={i}>
                <StyledHeading>{heading}</StyledHeading>
                <StyledList>
                  {links.map((link, i) => (
                    <StyledListItem key={i}>
                      <StyledListLink to={link.pageUrl}>
                        {link.text}
                      </StyledListLink>
                    </StyledListItem>
                  ))}
                </StyledList>
              </StyledGroup>
            ))}
            <StyledGroup>
              <StyledHeading>Contact</StyledHeading>
              <StyledContact>
                {address}
                <br />
                Tel: <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                <br />
                Email: <a href={`mailto:${emailAddress}`}>{emailAddress}</a>
              </StyledContact>
              {(twitterProfile ||
                linkedinProfile ||
                facebookProfile ||
                instagramProfile) && (
                <StyledSocial>
                  {twitterProfile && (
                    <StyledSocialLink
                      href={twitterProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Twitter Profile"
                    >
                      <StyledIcon image={twitterIcon} />
                    </StyledSocialLink>
                  )}
                  {linkedinProfile && (
                    <StyledSocialLink
                      href={linkedinProfile}
                      target="_blank"
                      rel="noopener"
                      aria-label="LinkedIn Profile"
                    >
                      <StyledIcon image={linkedinIcon} />
                    </StyledSocialLink>
                  )}
                  {facebookProfile && (
                    <StyledSocialLink
                      href={facebookProfile}
                      target="_blank"
                      rel="noopener"
                      aria-label="Facebook Profile"
                    >
                      <StyledIcon image={facebookIcon} />
                    </StyledSocialLink>
                  )}
                  {instagramProfile && (
                    <StyledSocialLink
                      href={instagramProfile}
                      target="_blank"
                      rel="noopener"
                      aria-label="Instagram Profile"
                    >
                      <StyledIcon image={instagramIcon} />
                    </StyledSocialLink>
                  )}
                </StyledSocial>
              )}
            </StyledGroup>
            <StyledGroup>
              <StyledAccreditations>
                <StyledAccreditation
                  src={isoIcon9001Icon}
                  alt="Dizions ISO 9001 2008 icon"
                  loading="lazy"
                />
                <StyledAccreditation
                  src={cyberEssentialsIcon}
                  alt="Cyber Essentials Icon"
                  loading="lazy"
                />
                <StyledAccreditation
                  src={isoIcon27001Icon}
                  alt="Dizions ISO 27001 2013 icon"
                  loading="lazy"
                />
              </StyledAccreditations>
              <StyledAccreditations>
                <StyledAccreditation
                  src={disabilityConfidentLogo}
                  alt="Disability Confident Logo"
                  loading="lazy"
                />
                <StyledAccreditation
                  src={acevoLogo}
                  alt="Acevo Premium Partner Logo"
                  loading="lazy"
                />
              </StyledAccreditations>
              <StyledAccreditations>
                <StyledAccreditation
                  src={madeinukLogo}
                  alt="Made In UK Logo"
                  loading="lazy"
                />
              </StyledAccreditations>
            </StyledGroup>
          </StyledMain>
          <StyledBottom>
            <StyledCopyright>
              All rights reserved &copy; Copyright Dizions Ltd. {siteName} is a
              trading name of Dizions Ltd. Registered in Scotland: SC340502
            </StyledCopyright>
            <StyledSigniture>
              Website by{' '}
              <a
                href="https://www.attachdigital.co.uk"
                target="_blank"
                rel="noopener"
              >
                Attach Digital
              </a>
            </StyledSigniture>
          </StyledBottom>
        </StyledInner>
      </StyledContainer>
    </StyledFooter>
  );
};

export default Footer;
